import { AddCircle, Settings } from '@mui/icons-material';
import ArrowDropDownSharpIcon from '@mui/icons-material/ArrowDropDownSharp';
import GridOnIcon from '@mui/icons-material/GridOn';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Container, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Select, Switch, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { usePDF } from 'react-to-pdf';
import { downloadCustomCsv } from 'src/common';
import { WO2Menu } from 'src/common/components/Menu';
import WO2Button from 'src/common/components/button/Button';
import { ServiceTypesEnum } from 'src/common/types';
import DateRangePicker from '../../../../../../common/components/DateRangePicker';
import PdfFooter from '../../../common/components/PdfFooter';
import PdfHeader from '../../../common/components/PdfHeader';
import { PortfolioVersionSelect } from '../../components/PortfolioVersionSelect';
import { Props } from '../container';
import { GroupingType, PortfolioViewMode, ProcessCalculateHoldingsAndPerformanceRequest, SecurityColumnVisibility } from '../store/types';
import AvailableCashCard from './AvailableCashCard';
import CostBaseCard from './CostBaseCard';
import { GroupingSelect } from './GroupingSelect';
import MarketValueCard from './MarketValueCard';
import { PortfolioDetails } from './PortfolioDetails';
import { PortfolioDetailsCompact } from './PortfolioDetailsCompact';
import { generateDetailedCsv } from './SecurityListTableHelper';
import UnrealisedGainsLossCard from './UnrealisedGainsLossCard';
import { CalculateHoldingsAndPerformanceDialog } from './calculateHoldingsAndPerformance/CalculateHoldingsAndPerformanceDialog';
import { NewPortfolioDialog, NewPortfolioDialogResponse } from './newPortfolio/newPortfolioDialog';

export const Overview = ({
  history,
  clientId,
  displayDetailedView,
  entityDetails,
  parameters,
  portfolioDetailResult,
  fetchPortfolioDetail,
  setParameters,
  portfolioAssetGroups,
  gainsLossesItems,
  marketValue,
  fetchPortfolioDetailProgress,
  fetchTemplates,
  templates,
  fetchTemplatesProgress,
  createAndGetNewInvestmentService,
  createAndGetNewInvestmentServiceProgress,
  investmentServicesWithVersionsGrouped,
  portfolioFromDate,
  common,
  setInvestmentServiceVersion,
  afslId,
  selectedInvestmentService,
  isDataFromTriumph,
  hasClientEditAdminOnlyPermission,
  hasClientDirectAccess,
  processCalculateHoldingsAndPerformance,
  processCalculateHoldingsAndPerformanceProgress,
  setPortfolioDetailedView,
}: Props): JSX.Element => {
  const [showTriumphTasks, setShowTriumphTasks] = useState<boolean>(false);
  const [newPortfolioDialogOpen, setNewPortfolioDialogOpen] = useState<boolean>(false);
  const [showPdfFooterAndHeader, setShowPdfFooterAndHeader] = useState(false);
  const [menuViewMode, setMenuViewMode] = useState<PortfolioViewMode>(displayDetailedView ? 'detailed' : 'compact');

  const [columnVisibility, setColumnVisibility] = useState<SecurityColumnVisibility>({
    name: true,
    securityCode: true,
    units: true,
    costBase: true,
    unitCost: true,
    unitPrice: true,
    nativePrice: false,
    currentValue: true,
    nativeValue: false,
    currentWeightPercentage: true,
    gainLossPercentage: true,
    returnSinceInception: true,
    returnSinceInceptionPercentage: true,
    gainLossValue: true,
    estimatedIncome: true,
    estimatedYield: true,
    estimatedGrossIncome: false,
    estimatedGrossYield: false,
    priceDate: true,
    currencyCode: false,
    forexRate: false,
    targetValue: false,
    targetWeight: false,
    differenceValue: false,
    differenceWeightPercentage: false,
    unsettledUnits: false,
    unsettledValue: false,
  });

  const { toPDF, targetRef } = usePDF({
    filename: entityDetails?.name + '_Portfolio',
    page: {
      orientation: 'landscape',
      margin: {
        top: 6,
        bottom: 6,
        left: 2,
        right: 6,
      },
    },
  });

  useEffect(() => {
    setMenuViewMode(menuViewMode);
  }, []);

  useEffect(() => {
    if (showPdfFooterAndHeader) {
      toPDF();
      setShowPdfFooterAndHeader(false);
    }
  }, [showPdfFooterAndHeader]);

  const generatePdf = () => {
    setShowPdfFooterAndHeader(true);
  };

  const exportButtons = [
    {
      icon: <PictureAsPdfOutlinedIcon />,
      label: 'Export to PDF',
      onClick: generatePdf,
      testId: `generatePdf`,
    },
    {
      icon: <GridOnIcon />,
      label: 'Export to CSV',
      onClick:
        menuViewMode === 'detailed'
          ? () => {
              downloadCustomCsv(generateDetailedCsv(portfolioDetailResult, parameters, columnVisibility), entityDetails?.name + '_Portfolio');
            }
          : () =>
              downloadCustomCsv(
                generateDetailedCsv(portfolioDetailResult, parameters, {
                  name: true,
                  securityCode: true,
                  units: true,
                  costBase: false,
                  unitCost: false,
                  unitPrice: true,
                  nativePrice: false,
                  currentValue: true,
                  nativeValue: false,
                  currentWeightPercentage: true,
                  gainLossPercentage: false,
                  returnSinceInception: false,
                  returnSinceInceptionPercentage: false,
                  gainLossValue: false,
                  estimatedIncome: false,
                  estimatedYield: false,
                  estimatedGrossIncome: false,
                  estimatedGrossYield: false,
                  priceDate: true,
                  currencyCode: false,
                  forexRate: false,
                  targetValue: true,
                  targetWeight: true,
                  differenceValue: true,
                  differenceWeightPercentage: true,
                  unsettledUnits: true,
                  unsettledValue: true,
                }),
                entityDetails?.name + '_Portfolio'
              ),
      testId: `generateCsv`,
    },
  ];

  useEffect(() => {
    if (!!clientId) {
      fetchPortfolioDetail({
        clientId,
        parameters,
        investmentServiceId: common.investmentServiceId,
        isDataFromTriumph: isDataFromTriumph,
        fromDate: portfolioFromDate,
      });
    }
  }, [clientId, parameters.dates, parameters.groupingType, parameters.showPositionsNotHeld, common.investmentServiceId, portfolioFromDate]);

  return (
    <Container style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      <Grid container spacing={2} direction="column">
        <Grid item container spacing={1} alignItems="center">
          <Grid item>
            <PortfolioVersionSelect
              items={investmentServicesWithVersionsGrouped}
              selectedInvestmentServiceVersion={
                investmentServicesWithVersionsGrouped.find((v) => v.investmentServiceId === common.investmentServiceId) || null
              }
              setInvestmentServiceId={(investmentServiceId, investmentServiceVersionId) => {
                const isAll = investmentServiceId === -1;

                setInvestmentServiceVersion({
                  investmentServiceId: isAll ? null : investmentServiceId,
                  investmentServiceVersionId,
                });
              }}
              optionForAll={true}
              hasClientEditAdminOnlyPermission={hasClientEditAdminOnlyPermission}
            />
          </Grid>
          {!hasClientDirectAccess && (
            <Grid item>
              <WO2Button
                color={'inherit'}
                style={{ color: 'rgba(0,0,0,0.54)' }}
                type="button"
                data-testid={`addPortfolioButton`}
                disableFocusRipple
                disableRipple
                onClick={() => {
                  setNewPortfolioDialogOpen(true);
                }}
              >
                <AddCircle></AddCircle>
                <Typography variant="inherit" style={{ marginLeft: '10px' }}>
                  ADD NEW
                </Typography>
              </WO2Button>
            </Grid>
          )}
          {!hasClientDirectAccess && (
            <Grid item>
              <WO2Button
                color={'inherit'}
                style={
                  investmentServicesWithVersionsGrouped && investmentServicesWithVersionsGrouped.length !== 0 ? { color: 'rgba(0,0,0,0.54)' } : {}
                }
                type="button"
                data-testid={`investmentServicesButton`}
                onClick={() => {
                  history.push(`./configuration`);
                }}
                disabled={!investmentServicesWithVersionsGrouped || investmentServicesWithVersionsGrouped.length === 0}
              >
                <Settings></Settings>
                <Typography variant="inherit" style={{ marginLeft: '10px' }}>
                  CONFIGURE
                </Typography>
              </WO2Button>
            </Grid>
          )}

          <Grid item flex={1} />

          <Grid item>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={parameters?.showPositionsNotHeld ?? false}
                    onClick={() => setParameters({ ...parameters, showPositionsNotHeld: !parameters?.showPositionsNotHeld })}
                  />
                }
                label="Show positions not held"
              />
            </FormGroup>
          </Grid>
          <Grid item>
            <DateRangePicker
              dateRange={parameters?.dates}
              setDateRange={(dates) => {
                setParameters({ ...parameters, dates });
              }}
              isDateOnly
            />
          </Grid>
          <Grid item>
            <GroupingSelect
              selectedItem={parameters?.groupingType}
              setSelectedItem={(groupingType) => {
                setParameters({ ...parameters, groupingType });
              }}
            />
          </Grid>
          <Grid item>
            <FormControl>
              <InputLabel id="view-mode">View</InputLabel>
              <Select
                labelId="view-mode"
                value={menuViewMode}
                label="View"
                onChange={(e) => {
                  if (e.target.value === 'detailed') {
                    setParameters({
                      ...parameters,
                      groupingType: GroupingType.AssetClass,
                      showPositionsNotHeld: false,
                    });
                  } else {
                    setParameters({ ...parameters, showPositionsNotHeld: true });
                  }
                  setMenuViewMode(e.target.value as PortfolioViewMode);
                  if ((e.target.value as PortfolioViewMode) === 'compact') {
                    setPortfolioDetailedView(false);
                  } else if ((e.target.value as PortfolioViewMode) === 'detailed') {
                    setPortfolioDetailedView(true);
                  }
                }}
              >
                <MenuItem value="compact">Compact</MenuItem>
                <MenuItem value="detailed">Detailed View</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {hasClientEditAdminOnlyPermission && (
            <Grid item>
              <WO2Button color={'primary'} disableElevation onClick={() => setShowTriumphTasks(true)} variant={'contained'} value="Continue">
                Calculate Holdings & Performance
              </WO2Button>
            </Grid>
          )}

          <Grid item>
            <WO2Menu
              testId="export_button"
              buttonTitle="Export"
              buttonIcon={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    backgroundColor: 'blue',
                    borderRadius: '25px',
                    padding: '10px 10px 10px 20px',
                  }}
                >
                  <span style={{ color: '#ffffff', fontSize: '0.875rem' }}>EXPORT</span>
                  <ArrowDropDownSharpIcon sx={{ color: '#ffffff' }} />
                </div>
              }
              items={exportButtons}
            />
          </Grid>
          {investmentServicesWithVersionsGrouped.find((v) => v.investmentServiceId === common.investmentServiceId)?.serviceTypeId !==
            ServiceTypesEnum.SMA.id &&
            !hasClientDirectAccess && (
              <Grid item>
                <Tooltip
                  arrow
                  title={
                    (selectedInvestmentService?.currentVersionStatus == 'Pending' || selectedInvestmentService?.currentVersionStatus == undefined) &&
                    selectedInvestmentService?.previousVersionStatus == null
                      ? 'Portfolio must be active to rebalance'
                      : selectedInvestmentService?.notAvailableToTrade === true
                      ? 'Placing orders for this client is not currently available as part of the trade freeze'
                      : ''
                  }
                  placement="top"
                >
                  <span>
                    <WO2Button
                      color={'primary'}
                      disableElevation
                      onClick={() => history.push(`/client/${clientId}/portfolio/rebalance`)}
                      variant={'contained'}
                      value="Continue"
                      disabled={
                        ((selectedInvestmentService?.currentVersionStatus == 'Pending' ||
                          selectedInvestmentService?.currentVersionStatus == undefined) &&
                          selectedInvestmentService?.previousVersionStatus == null) ||
                        selectedInvestmentService?.notAvailableToTrade === true
                      }
                    >
                      Rebalance
                    </WO2Button>
                  </span>
                </Tooltip>
              </Grid>
            )}
        </Grid>

        {showTriumphTasks && (
          <CalculateHoldingsAndPerformanceDialog
            onClose={() => {
              setShowTriumphTasks(false);
            }}
            submitTriumphRequest={(request: ProcessCalculateHoldingsAndPerformanceRequest) => {
              processCalculateHoldingsAndPerformance(request);
            }}
            submitTriumphProgress={processCalculateHoldingsAndPerformanceProgress}
            clientId={clientId}
          />
        )}

        {newPortfolioDialogOpen && (
          <NewPortfolioDialog
            fetchTemplates={() => {
              !!afslId && fetchTemplates({ afslId });
            }}
            fetchTemplatesProgress={fetchTemplatesProgress}
            onClose={() => {
              if (!!afslId) {
                fetchTemplates({ afslId });
                setNewPortfolioDialogOpen(false);
              }
            }}
            onCreate={async (payload: NewPortfolioDialogResponse) => {
              if (!!clientId && payload.serviceTypeId) {
                await createAndGetNewInvestmentService({
                  clientId,
                  investmentServiceTemplateVersionId: payload.template?.investmentServiceTemplateVersionId || null,
                  serviceTypeId: payload.serviceTypeId,
                });
                history.push(`./configuration`);
              }
            }}
            templates={templates}
            createProgress={createAndGetNewInvestmentServiceProgress}
          />
        )}

        <Grid item container spacing={2} direction="column" ref={targetRef}>
          {showPdfFooterAndHeader && <PdfHeader clientName={entityDetails?.name + ' - Portfolio'} />}
          {menuViewMode === 'compact' && (
            <Grid item container spacing={2}>
              <Grid item xs={3}>
                <CostBaseCard />
              </Grid>
              <Grid item xs={3}>
                <UnrealisedGainsLossCard />
              </Grid>
              <Grid item xs={3}>
                <MarketValueCard />
              </Grid>
              <Grid item xs={3}>
                <AvailableCashCard />
              </Grid>
            </Grid>
          )}
          <Grid item>
            {menuViewMode === 'detailed' ? (
              <PortfolioDetails
                clientId={clientId}
                parameters={parameters}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                portfolioAssetGroups={portfolioAssetGroups}
                gainsLossesItems={gainsLossesItems}
                portfolioDetailResult={portfolioDetailResult}
                setParameters={setParameters}
                totalValue={marketValue}
                progress={fetchPortfolioDetailProgress}
              />
            ) : (
              <PortfolioDetailsCompact
                clientId={clientId}
                parameters={parameters}
                portfolioAssetGroups={portfolioAssetGroups}
                gainsLossesItems={gainsLossesItems}
                totalValue={marketValue}
                progress={fetchPortfolioDetailProgress}
              />
            )}
          </Grid>
          {showPdfFooterAndHeader && <PdfFooter />}
        </Grid>
      </Grid>
    </Container>
  );
};
